/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { useRouter } from 'next/router';
import React, {
    Fragment,
    FunctionComponent,
    useContext,
    useState,
} from 'react';
import styled from 'styled-components';

import { MediaQuery } from '@toucan/helpers/media-queries';

import IntercomSvg from '@toucan/images/intercom.svg';

import { IntercomScript } from '@toucan/client/features/intercom/components/intercom-script';
import { ApplicationContext } from '@toucan/client/global/application';
import { useMediaQuery } from '@toucan/client/shared/hooks/use-media-query';

import { styles } from './intercom.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface IntercomProps {
    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

export type StyledIntercomProps = IntercomProps & {
    stickyFooterHeight: number;
    shouldDisplayCustomLauncher: boolean;
};

const StyledIntercom = styled('button').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const pathnamesToExcludeOnSmallDisplays = [
    '/',
    '/basket',
    '/sell-form',
    '/quote-summary',
    '/quote-book-in',
    '/payment-link',
    '/checkout',
];

/**
 * Checks pathname against a list of template paths.
 * If the pathname is in the list of exluded pathnames then we return false
 */
const isValidUrl = (path: string = '') => {
    return !pathnamesToExcludeOnSmallDisplays.some((excludedPath) => {
        const pathnameSlugParts = path.split('/');
        const excludedSlugParts = excludedPath.split('/');
        return pathnameSlugParts?.[1] === excludedSlugParts?.[1];
    });
};

/**
 * Intercom button, with lazy loading of the Intercom script
 */
export const Intercom: FunctionComponent<IntercomProps> = ({
    className,
    ...otherProps
}: IntercomProps) => {
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);
    const { stickyFooterHeight } = useContext(ApplicationContext);
    const [enabled, setEnabled] = useState(false);

    const handleIntercomClick = () => {
        setEnabled(true);
    };

    const router = useRouter();

    const isValidUrlForSmallDisplays = isValidUrl(router.pathname);

    // if it's small display and URL isn't excluded OR any large display
    const shouldDisplayCustomLauncher =
        (isValidUrlForSmallDisplays && isSmallTablet) || !isSmallTablet;

    const preparedClassName = `${className} intercom-trigger`;

    return (
        <Fragment>
            <IntercomScript
                enabled={enabled}
                verticalPadding={stickyFooterHeight + 20}
            />
            <StyledIntercom
                type="button"
                className={preparedClassName}
                onClick={handleIntercomClick}
                stickyFooterHeight={stickyFooterHeight}
                shouldDisplayCustomLauncher={shouldDisplayCustomLauncher}
                {...otherProps}
            >
                <IntercomSvg />
            </StyledIntercom>
        </Fragment>
    );
};
